// Las seis preguntas del perfil de PLD (ocupación, ingreso y los 4 MXN_*), compartidas por la
// Bienvenida de la venta y el paso 02 de la apertura de cuenta.
import { Button } from '@/kit';
import { cn } from '@/lib/cn';
import { MONTHLY_INCOME_PAIRS, OCCUPATION_PAIRS } from '../profileCatalog';

/**
 * Opciones de las preguntas de PLD. Los VALORES son los del catálogo de Fiado (`AppSelectionData_GT`)
 * y el DTO del wizard los valida con `@IsIn`; las etiquetas viven aquí hasta que exista su proxy.
 * `usageMonthly` es FRECUENCIA de uso (catálogo `Frequency`) y `beneficialOwner` sale de `ActMXN`.
 */
export const PROFILE_QUESTION_OPTIONS = {
    occupation: OCCUPATION_PAIRS,
    monthlyIncome: MONTHLY_INCOME_PAIRS,
    monthlyExtraSupport: [
        ['0', 'Nada'],
        ['2000', 'Menos de $2,000'],
        ['10000', 'Menos de $10,000'],
        ['20000', 'Menos de $20,000'],
        ['50000', 'Menos de $50,000'],
        ['60000', 'Más de $50,000'],
    ],
    usageMonthly: [
        ['60', '2 al día'],
        ['90', '3 al día'],
        ['150', '5 al día'],
        ['180', '6 al día'],
        ['210', '7 al día'],
        ['240', '8 al día'],
        ['270', '9 al día'],
        ['300', '10 al día'],
    ],
    maximumTx: [
        ['10000', 'Hasta $10,000'],
        ['100000', 'Hasta $100,000'],
        ['300000', 'Hasta $300,000'],
    ],
    beneficialOwner: [
        ['SELF', 'Sí'],
        ['OTHER', 'No, de un tercero'],
    ],
} as const;

export type ProfileQuestionKey = keyof typeof PROFILE_QUESTION_OPTIONS;

export const PROFILE_QUESTION_KEYS = Object.keys(PROFILE_QUESTION_OPTIONS) as ProfileQuestionKey[];

export const PROFILE_QUESTION_LABELS: Record<ProfileQuestionKey, string> = {
    occupation: 'Ocupación',
    monthlyIncome: 'Ingreso mensual',
    monthlyExtraSupport: 'Apoyo económico extra',
    usageMonthly: 'Frecuencia de uso',
    maximumTx: 'Monto máximo por operación',
    beneficialOwner: '¿El dinero es suyo?',
};

const DEFAULT_HINT =
    'Revísalas con el cliente y corrige lo que no coincida. Es lo que queda en su expediente.';

/**
 * Las seis preguntas en dos columnas. Sin `onSave` no pinta botón: el paso que la usa decide su
 * acción. `invalid` marca las que el silo reportó como faltantes.
 */
export function ProfileQuestions({
    value,
    onChange,
    canSave = false,
    saving = false,
    onSave,
    invalid = [],
    invalidHint,
    hint = DEFAULT_HINT,
}: {
    value: (k: ProfileQuestionKey) => string;
    onChange: (k: ProfileQuestionKey, answer: string) => void;
    canSave?: boolean;
    saving?: boolean;
    onSave?: () => void;
    invalid?: readonly string[];
    invalidHint?: string;
    hint?: string;
}) {
    return (
        <>
            <p className="mb-3 text-[11.5px] leading-relaxed text-muted-foreground">{hint}</p>
            <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {PROFILE_QUESTION_KEYS.map((k) => {
                    const isInvalid = invalid.includes(k);
                    const hintId = `profile-question-${k}-error`;
                    return (
                        <div key={k} className="text-[11.5px]">
                            <label className="block">
                                <span className="mb-1 block text-muted-foreground">
                                    {PROFILE_QUESTION_LABELS[k]}
                                </span>
                                <select
                                    className={cn(
                                        'w-full rounded-md border bg-card px-2 py-1.5 text-[12.5px]',
                                        isInvalid ? 'border-destructive' : 'border-border',
                                    )}
                                    value={value(k)}
                                    aria-invalid={isInvalid || undefined}
                                    aria-describedby={isInvalid && invalidHint ? hintId : undefined}
                                    onChange={(e) => {
                                        onChange(k, e.target.value);
                                    }}
                                >
                                    <option value="">Selecciona…</option>
                                    {PROFILE_QUESTION_OPTIONS[k].map(([optionValue, text]) => (
                                        <option key={optionValue} value={optionValue}>
                                            {text}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            {isInvalid && invalidHint && (
                                <span id={hintId} className="mt-1 block text-destructive">
                                    {invalidHint}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
            {/* Con dinero de un tercero la cuenta NO abre: se avisa antes de guardar. */}
            {value('beneficialOwner') === 'OTHER' && (
                <p className="mb-2 rounded-md border border-warning-soft-border bg-warning-soft/40 px-3 py-2 text-[11.5px] leading-relaxed">
                    Si el dinero es de otra persona, la cuenta no puede abrirse. Verifícalo con el
                    cliente antes de continuar.
                </p>
            )}
            {onSave && (
                <Button size="sm" disabled={!canSave} isLoading={saving} onClick={onSave}>
                    {saving ? 'Guardando…' : 'Guardar perfil'}
                </Button>
            )}
        </>
    );
}
